import {
  Box,
  Button,
  Flex,
  SimpleGrid,
  Text,
  Icon,
  Badge,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  Menu,
  MenuButton,
  MenuList,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { Layout } from "@/components/Layout/Layout";
import { useEffect, useState } from "react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { FiEdit, FiTrash2, FiClock, FiUser, FiGlobe, FiCalendar } from "react-icons/fi";
import { ViewInformationBlock } from "@/components/ViewInformationBlock/ViewInformationBlock";
import { Lead } from "@/model/lead";
import api from "@/api";
import { formatDate } from "@/helpers/formatDate";
import PageLoaderWrapper from "@/components/PageLoaderWrapper";

export const SingleLeadPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [currentLead, setCurrentLead] = useState<Lead | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const loadLead = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        const response = await api.leads.lead({
          params: {
            id: Number(id),
            expand: ["status", "responsible_user", "source", "contact", "site", "comments", "nda", "form", "nda_call", "created_at"],
          },
        });
        setCurrentLead(response.data);
      } catch (error) {
        setCurrentLead(null);
      } finally {
        setIsLoading(false);
      }
    };
    loadLead();
  }, [id]);

  const handleDelete = () => {
    setIsDeleting(true);
    setTimeout(() => {
      setIsDeleting(false);
      setDeleteOpen(false);
      navigate("/leads");
    }, 500);
  };

  return (
    <Layout>
      <PageLoaderWrapper isLoading={isLoading} loadingText="Загрузка данных лида...">
        {!currentLead ? (
          <Flex justifyContent="center" alignItems="center" minH="50vh">
            <Text>Лид не найден</Text>
          </Flex>
        ) : (
          <Flex flexDirection="column" gap="24px" paddingRight="32px" maxH="90vh" overflowY="auto">
        <Text color="gray.500" cursor="pointer" onClick={() => navigate("/leads")} display="flex" alignItems="center" gap="8px">
          <ArrowBackIcon />
          Назад к лидам
        </Text>

        <Flex alignItems="center" justifyContent="space-between">
          <Text fontSize={30} fontWeight={700} color="#3b515e">
            {currentLead.name}
          </Text>
          <Flex gap="16px">
            <Button leftIcon={<Icon as={FiEdit as any} boxSize={4} />} onClick={() => navigate(`/leads/${id}/edit`)}>
              Редактировать
            </Button>
            <Menu>
              <MenuButton leftIcon={<Icon as={FiClock as any} boxSize={4} />} as={Button} variant="outline">
                История
              </MenuButton>
              <MenuList maxH="400px" overflowY="auto" w="400px">
                <Box p="12px">
                  <Text fontSize="sm" color="gray.500">
                    История изменений пуста
                  </Text>
                </Box>
              </MenuList>
            </Menu>
            <Button leftIcon={<Icon as={FiTrash2 as any} boxSize={4} />} colorScheme="red" onClick={() => setDeleteOpen(true)}>
              Удалить
            </Button>
          </Flex>
        </Flex>

        <ViewInformationBlock icon={<Icon as={FiUser as any} boxSize={5} color="#3b515e" />} title="Информация о лиде">
          <Flex flexDirection="column" gap="24px">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Field label="Название" value={currentLead.name} icon={<Icon as={FiUser as any} boxSize={4} color="#65758b" />} />
              <Field
                label="Статус"
                value={
                  currentLead.status ? (
                    <Badge bg="#3B82F6" color="white" px="12px" py="4px" borderRadius="16px" fontSize="14px" fontWeight={500}>
                      {currentLead.status.name}
                    </Badge>
                  ) : (
                    "—"
                  )
                }
              />
              <Field
                label="Ответственный"
                value={currentLead.responsible_user?.name || "Не назначен"}
                icon={<Icon as={FiUser as any} boxSize={4} color="#65758b" />}
              />
              <Field label="Источник лида" value={currentLead.source?.name || "—"} />
              <Field label="Контакты" value={currentLead.contact ? `${currentLead.contact.name} (${currentLead.contact.phone})` : "—"} />
              <Field
                label="Сайт"
                value={
                  currentLead.site && currentLead.site !== "—" ? (
                    <Flex alignItems="center" gap="8px">
                      <Icon as={FiGlobe as any} boxSize={4} color="#65758b" />
                      <a
                        href={currentLead.site.startsWith("http") ? currentLead.site : `https://${currentLead.site}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: "#2563EB", textDecoration: "none" }}
                      >
                        {currentLead.site}
                      </a>
                    </Flex>
                  ) : (
                    "—"
                  )
                }
              />
            </SimpleGrid>

            <Box borderTop="1px solid #E5E7EB" pt="16px">
              <Text fontSize="14px" color="#6B7280" fontWeight={700} mb="12px">
                Документы и согласования
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                <Field label="NDA" value={currentLead.nda ? "Да" : "Нет"} />
                <Field label="Анкета" value={currentLead.form ? "Да" : "Нет"} />
                <Field label="Созвон NDA" value={currentLead.nda_call ? "Да" : "Нет"} />
              </SimpleGrid>
            </Box>

            {currentLead.created_at && (
              <Box borderTop="1px solid #E5E7EB" pt="16px">
                <Text fontSize="14px" color="#6B7280" fontWeight={700} mb="12px">
                  Дата создания
                </Text>
                <Flex alignItems="center" gap="8px">
                  <Icon as={FiCalendar as any} boxSize={4} color="#65758b" />
                  <Text color="#3b515e" fontSize={16} lineHeight="24px">
                    {formatDate(String(currentLead.created_at))}
                  </Text>
                </Flex>
              </Box>
            )}
          </Flex>
        </ViewInformationBlock>

        <ViewInformationBlock icon={<></>} title="Комментарий">
          <Text color="#4a5568" fontSize="14px" lineHeight="1.5" whiteSpace="pre-line">
            {currentLead.comments || "—"}
          </Text>
        </ViewInformationBlock>

        <Modal isCentered isOpen={isDeleteOpen} onClose={() => setDeleteOpen(false)}>
          <ModalOverlay backdropFilter="blur(3px)" />
          <ModalContent maxW="50vw" gap="24px" px="24px" paddingTop="32px" paddingBottom="24px">
            <ModalHeader p={0}>Подтвердите удаление</ModalHeader>
            <ModalBody>
              <Text>
                Вы действительно хотите удалить лид <b>{currentLead.name}</b>?
              </Text>
            </ModalBody>
            <ModalFooter p={0} pt="8px">
              <Flex gap="12px">
                <Button variant="outline" onClick={() => setDeleteOpen(false)}>
                  Отмена
                </Button>
                <Button colorScheme="red" isLoading={isDeleting} onClick={handleDelete}>
                  Удалить
                </Button>
              </Flex>
            </ModalFooter>
          </ModalContent>
        </Modal>
          </Flex>
        )}
      </PageLoaderWrapper>
    </Layout>
  );
};

function Field({
  label,
  value,
  icon,
}: {
  label: string;
  value: string | React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <Flex flexDirection="column" gap="6px" minW={0}>
      <Text color="#65758b" fontSize={14} lineHeight="20px" fontWeight={500}>
        {label}
      </Text>
      <Flex alignItems="center" gap="8px">
        {icon}
        {typeof value === "string" ? (
          <Text color="#3b515e" fontSize={16} lineHeight="24px" fontWeight={400}>
            {value}
          </Text>
        ) : (
          value
        )}
      </Flex>
    </Flex>
  );
}

